export type CurriculumSourceKind = 'national-curriculum' | 'global-grade-benchmark';

export interface CurriculumSource {
  id: string;
  kind: CurriculumSourceKind;
  authority: string;
  title: string;
  reference: string;
  scope: string;
  url: string;
}

export interface SkillAlignment {
  sourceId: string;
  locator: string;
  alignment: string;
}

export interface CurriculumSkill {
  id: string;
  title: string;
  domain: string;
  stage: string;
  status: 'implemented' | 'planned' | 'experimental';
  exerciseFamily: string;
  alignments: readonly SkillAlignment[];
}

export const CURRICULUM_SOURCES: readonly CurriculumSource[] = [
  {
    id: 'it-mim-dm-221-2025',
    kind: 'national-curriculum',
    authority: 'Ministero dell’Istruzione e del Merito · Gazzetta Ufficiale',
    title: 'Indicazioni nazionali per il curricolo della scuola dell’infanzia e del primo ciclo d’istruzione',
    reference: 'D.M. 9 dicembre 2025, n. 221 · in applicazione dalle classi prime dall’a.s. 2026/2027',
    scope: 'Vincolo nazionale italiano. Gli OSA sono esplicitati a traguardi pluriennali (per esempio classe terza e quinta): i limiti numerici e le fasce d’età dell’app sono scelte di progressione conservative, non prescrizioni ministeriali.',
    url: 'https://www.gazzettaufficiale.it/eli/id/2026/01/27/26G00021/SG'
  },
  {
    id: 'unesco-gpf-math',
    kind: 'global-grade-benchmark',
    authority: 'UNESCO Institute for Statistics · Global Alliance to Monitor Learning',
    title: 'Global Proficiency Framework for Mathematics',
    reference: 'Grade 1 · Number and Operations · N1.2.1_P',
    scope: 'Benchmark internazionale grade-specific. Fornisce granularità per la prima classe senza sostituire il curricolo nazionale.',
    url: 'https://gaml.uis.unesco.org/wp-content/uploads/sites/2/2021/03/Global-Proficiency-Framework-Math.pdf'
  }
] as const;

const MIM_NUMBERS = 'Allegato · Matematica · Scuola primaria · OSA al termine della classe terza · Numeri';
const MIM_GEOMETRY = 'Allegato · Matematica · Scuola primaria · OSA al termine della classe terza · Spazio e figure';
const MIM_ITALIAN = 'Allegato · Italiano · Scuola primaria · OSA al termine della classe terza · Lingua e letteratura';

export const CURRICULUM_SKILLS: readonly CurriculumSkill[] = [
  {
    id: 'math.number-sense.quantity-to-numeral.1-5',
    title: 'Associare quantità e numerale da 1 a 5',
    domain: 'Matematica · Numeri',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'quantity-to-numeral',
    alignments: [
      {
        sourceId: 'it-mim-dm-221-2025',
        locator: MIM_NUMBERS,
        alignment: 'È coerente con il percorso nazionale su conteggio di oggetti/eventi, numeri naturali e significato cardinale del numero.'
      },
      {
        sourceId: 'unesco-gpf-math',
        locator: 'Grade 1 Mathematics · N1.2.1_P',
        alignment: 'Il livello iniziale richiede di riconoscere l’equivalenza tra quantità fino a 5 rappresentate come oggetti, immagini e numerali.'
      }
    ]
  },
  {
    id: 'math.number-sense.compare-quantities.1-5',
    title: 'Confrontare piccole quantità',
    domain: 'Matematica · Numeri',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'compare-quantities',
    alignments: [
      {
        sourceId: 'it-mim-dm-221-2025',
        locator: MIM_NUMBERS,
        alignment: 'Prepara il confronto e l’ordinamento dei numeri naturali partendo da rappresentazioni concrete di piccole quantità.'
      },
      {
        sourceId: 'unesco-gpf-math',
        locator: 'Grade 1 Mathematics · Number and Operations',
        alignment: 'Il confronto visivo di piccole quantità sostiene il number sense grade-specific prima del passaggio alla rappresentazione simbolica.'
      }
    ]
  },
  {
    id: 'math.patterns.simple-sequence',
    title: 'Riconoscere e continuare semplici regolarità',
    domain: 'Matematica · Relazioni e regolarità',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'simple-sequence',
    alignments: [
      {
        sourceId: 'it-mim-dm-221-2025',
        locator: 'Allegato · Matematica · Scuola primaria · regolarità in sequenze di numeri o figure',
        alignment: 'L’attività esercita direttamente il riconoscimento e la descrizione di una regolarità in una sequenza di figure.'
      }
    ]
  },
  {
    id: 'school.attention.one-step-instruction',
    title: 'Ascoltare ed eseguire una semplice istruzione',
    domain: 'Competenze scolastiche · Ascolto e attenzione',
    stage: 'Ingresso alla primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'one-step-instruction',
    alignments: [
      {
        sourceId: 'it-mim-dm-221-2025',
        locator: 'Allegato · Scuola primaria · Informatica · sequenze di passi e loro esecuzione',
        alignment: 'L’esecuzione di una consegna orale semplice allena la comprensione di un passo preciso e non ambiguo prima di sequenze più articolate.'
      }
    ]
  },
  {
    id: 'math.number-sense.successor.1-10',
    title: 'Individuare il numero successivo entro 10',
    domain: 'Matematica · Numeri',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'number-successor-1-10',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: MIM_NUMBERS, alignment: 'Implementa in un intervallo iniziale il conteggio progressivo e l’aspetto ricorsivo del numero naturale.' }]
  },
  {
    id: 'math.number-sense.predecessor.1-10',
    title: 'Individuare il numero precedente entro 10',
    domain: 'Matematica · Numeri',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'number-predecessor-1-10',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: MIM_NUMBERS, alignment: 'Implementa in un intervallo iniziale il conteggio regressivo e l’aspetto ricorsivo del numero naturale.' }]
  },
  {
    id: 'math.number-sense.compare-numerals.1-10',
    title: 'Confrontare numerali entro 10',
    domain: 'Matematica · Numeri',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'greater-numeral-1-10',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: MIM_NUMBERS, alignment: 'Esercita direttamente il confronto e l’ordinamento dei numeri naturali, con un limite iniziale entro 10 scelto come progressione dell’app.' }]
  },
  {
    id: 'math.number-sense.between.1-10',
    title: 'Collocare un numero tra precedente e successivo entro 10',
    domain: 'Matematica · Numeri',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'number-between-1-10',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: MIM_NUMBERS, alignment: 'Allena l’ordinamento dei numeri naturali e la loro relazione reciproca in una sequenza.' }]
  },
  {
    id: 'math.operations.visual-addition.within-5',
    title: 'Risolvere semplici addizioni visuali entro 5',
    domain: 'Matematica · Numeri e operazioni',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'visual-addition-within-5',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: MIM_NUMBERS, alignment: 'Propone una rappresentazione concreta e volutamente limitata delle semplici addizioni con numeri naturali previste dagli OSA.' }]
  },
  {
    id: 'math.operations.visual-subtraction.within-5',
    title: 'Risolvere semplici sottrazioni visuali entro 5',
    domain: 'Matematica · Numeri e operazioni',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'visual-subtraction-within-5',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: MIM_NUMBERS, alignment: 'Propone una rappresentazione concreta e volutamente limitata delle semplici sottrazioni con numeri naturali previste dagli OSA.' }]
  },
  {
    id: 'math.geometry.recognize-basic-shapes',
    title: 'Riconoscere e denominare figure geometriche di base',
    domain: 'Matematica · Spazio e figure',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'recognize-basic-shapes',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: MIM_GEOMETRY, alignment: 'Esercita il riconoscimento e la denominazione delle principali figure geometriche.' }]
  },
  {
    id: 'math.geometry.shape-properties.sides',
    title: 'Riconoscere figure in base al numero di lati',
    domain: 'Matematica · Spazio e figure',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'shape-by-sides',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: MIM_GEOMETRY, alignment: 'Esercita la descrizione delle figure secondo caratteristiche esplicitamente richiamate dagli OSA, tra cui il numero di lati.' }]
  },
  {
    id: 'italian.alphabet.order.next',
    title: 'Individuare la lettera successiva nell’alfabeto',
    domain: 'Italiano · Alfabeto',
    stage: 'Prima primaria · alfabetizzazione iniziale',
    status: 'implemented',
    exerciseFamily: 'italian-alphabet-next',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: `${MIM_ITALIAN} · Alfabeto`, alignment: 'Esercita l’ordine convenzionale delle lettere previsto dagli OSA.' }]
  },
  {
    id: 'italian.alphabet.recognize-vowels',
    title: 'Riconoscere le vocali tra lettere',
    domain: 'Italiano · Alfabeto',
    stage: 'Prima primaria · alfabetizzazione iniziale',
    status: 'implemented',
    exerciseFamily: 'italian-recognize-vowels',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: `${MIM_ITALIAN} · Alfabeto e alfabetizzazione di base`, alignment: 'Attività elementare di discriminazione delle lettere a supporto dell’alfabetizzazione di base.' }]
  },
  {
    id: 'italian.phonics.initial-letter',
    title: 'Associare parola e lettera iniziale',
    domain: 'Italiano · Fonemi e grafemi',
    stage: 'Prima primaria · alfabetizzazione iniziale',
    status: 'implemented',
    exerciseFamily: 'italian-word-initial-letter',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: `${MIM_ITALIAN} · Grafia dell’italiano`, alignment: 'Collega parole semplici alla loro lettera iniziale come esercizio controllato sul rapporto tra aspetto sonoro e grafema.' }]
  },
  {
    id: 'italian.phonics.syllable-count',
    title: 'Riconoscere il numero di sillabe in parole semplici',
    domain: 'Italiano · Sillabe',
    stage: 'Prima primaria · alfabetizzazione iniziale',
    status: 'implemented',
    exerciseFamily: 'italian-syllable-count',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: `${MIM_ITALIAN} · Grafia dell’italiano · divisione in sillabe`, alignment: 'Prepara e pratica la divisione in sillabe con un lessico chiuso e verificato.' }]
  },
  {
    id: 'italian.writing.punctuation.basic',
    title: 'Scegliere il segno finale appropriato',
    domain: 'Italiano · Interpunzione',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'italian-punctuation-basic',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: `${MIM_ITALIAN} · Interpunzione, segni grafici e paragrafematici`, alignment: 'Esercita in forma semplice punto, punto interrogativo e punto esclamativo, esplicitamente richiamati dagli OSA.' }]
  },
  {
    id: 'italian.listening.two-step-instruction',
    title: 'Comprendere una semplice istruzione in due passi',
    domain: 'Italiano · Ascolto e dialogo',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'italian-listen-two-step',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: `${MIM_ITALIAN} · Ascolto e dialogo`, alignment: 'Estende gradualmente la comprensione di semplici istruzioni su attività note senza introdurre valutazione clinica dell’attenzione.' }]
  },
  {
    id: 'italian.reading.identify-character',
    title: 'Individuare il personaggio in un brevissimo testo',
    domain: 'Italiano · Lettura e interpretazione',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'italian-short-text-character',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: `${MIM_ITALIAN} · Lettura e interpretazione`, alignment: 'Esercita l’individuazione di elementi essenziali del testo, in particolare i personaggi.' }]
  },
  {
    id: 'italian.reading.identify-place',
    title: 'Individuare il luogo in un brevissimo testo',
    domain: 'Italiano · Lettura e interpretazione',
    stage: 'Prima primaria · livello iniziale',
    status: 'implemented',
    exerciseFamily: 'italian-short-text-place',
    alignments: [{ sourceId: 'it-mim-dm-221-2025', locator: `${MIM_ITALIAN} · Lettura e interpretazione`, alignment: 'Esercita l’individuazione di elementi essenziali del testo, in particolare i luoghi.' }]
  }
] as const;
